import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import { formatMobile, formatName, formatOfficeName } from "../../../lib/Misc";
import { debounce } from "lodash";
import MainBody from "../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import sortCaret from "../../../components/SortCaret";
import AddEditUser from "./AddEditUser";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import { BadgeCheck, BanIcon, LucidePlusCircle } from "lucide-react";
import UserActionButton from "./UserActionButton";
import Confirmation from "../../../components/Confirmation";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import UnauthorisedAccess from "../../../UnauthorisedAccess";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/use-toast";

const Users = ({ governance = false, expandList = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { organizationData } = useOrganization();

  const [userId, setUserId] = useState(null);
  const [showAddEditUser, setShowAddEditUser] = useState(false);
  const [showUserDelete, setShowUserDelete] = useState(false);
  const [showUserResend, setShowUserResend] = useState(false);
  const [canCreate, setCanCreate] = useState(null);
  const [userAllowedData, setUserAllowedData] = useState();

  const toggleShowAddEditUser = () => {
    if (showAddEditUser) {
      setEdit(false);
      setUserId(null);
    }
    setShowAddEditUser(!showAddEditUser);
  };

  const toggleShowDelete = () => {
    if (showUserDelete) {
      setUserId(null);
    }
    setShowUserDelete(!showUserDelete);
  };

  const toggleShowResend = () => {
    if (showUserResend) {
      setUserId(null);
    }
    setShowUserResend(!showUserResend);
  };

  const fetchUser = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.USER}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&organization_id=${
            organizationData?.organizationId
              ? organizationData?.organizationId
              : ""
          }${governance ? `&is_compliance=1&is_senior_manager=1` : ""}`,
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        setUserAllowedData(data);

        const usersList = data.data.data;
        setUsersList(usersList);
        setCanCreate(data.can_create);
        return data.data;
      } catch (error) {
        setLoading(false);
        setUsersList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField],
  );

  const usersGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: usersList[i]?.id,
        name: formatName(usersList[i]?.first_name, usersList[i]?.last_name),
        email: usersList[i]?.email,
        mobile: usersList[i]?.mobile ? formatMobile(usersList[i]?.mobile) : "-",
        role: usersList[i]?.role?.name ? usersList[i]?.role?.name : "",
        office: usersList[i]?.office?.street
          ? formatOfficeName(
              usersList[i]?.office?.street,
              usersList[i]?.office?.suburb,
            )
          : "-",
        deleted_at: usersList[i]?.deleted_at ? usersList[i]?.deleted_at : "",
        is_active: usersList[i]?.deleted_at ? (
          <div className="text-center align-middle">
            <div className="flex items-center justify-center">
              <BanIcon className="text-red-500" size={16} />
            </div>
          </div>
        ) : usersList[i]?.is_active ? (
          <div className="text-center align-middle">
            <div className="flex items-center justify-center">
              <BadgeCheck className="text-green-500" />
            </div>
          </div>
        ) : (
          ""
        ),

        is_compliance: usersList[i]?.is_compliance ? (
          <div className="text-center align-middle">
            <div className="flex items-center justify-center">
              <BadgeCheck className="text-green-500" />
            </div>
          </div>
        ) : (
          <div className="text-center align-middle">
            <div className="flex items-center justify-center">
              <BanIcon className="text-red-500" size={16} />
            </div>
          </div>
        ),

        is_senior_manager: usersList[i]?.is_senior_manager ? (
          <div className="text-center align-middle">
            <div className="flex items-center justify-center">
              <BadgeCheck className="text-green-500" />
            </div>
          </div>
        ) : (
          <div className="text-center align-middle">
            <div className="flex items-center justify-center">
              <BanIcon className="text-red-500" size={16} />
            </div>
          </div>
        ),
        action: (
          <>
            <UserActionButton
              id={usersList[i]?.id}
              setEdit={setEdit}
              setUserId={setUserId}
              userData={usersList[i]}
              toggleShowAddEditUser={toggleShowAddEditUser}
              toggleShowDelete={toggleShowDelete}
              fetchUser={fetchUser}
              admin={usersList[i]?.role?.slug === "admin" ? true : false}
              deletedUser={usersList[i]?.deleted_at}
              toggleShowResend={toggleShowResend}
              activeUser={usersList[i]?.is_active}
            />
          </>
        ),
      });
    }
    return items;
  };

  const users = usersGenerator(usersList?.length);

  const columns = governance
    ? [
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "name",
          text: "Name",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "email",
          text: "Email",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "mobile",
          text: "Mobile No.",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "office",
          text: "Office",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "role",
          text: "Role",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "is_active",
          text: "Status",
          sort: true,
          sortCaret,
          align: "center",
          headerAlign: "center",
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "is_compliance",
          text: "Compliance Manager",
          sort: true,
          sortCaret,
          align: "center",
          headerAlign: "center",
          style: { width: "127px" },
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "is_senior_manager",
          text: "Senior Manager",
          sort: true,
          sortCaret,
          align: "center",
          headerAlign: "center",
          style: { width: "127px" },
        },
      ]
    : [
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "name",
          text: "Name",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "email",
          text: "Email",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "mobile",
          text: "Mobile No.",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "office",
          text: "Office",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "role",
          text: "Role",
          sort: true,
          sortCaret,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "is_active",
          text: "Status",
          sort: true,
          sortCaret,
          align: "center",
          headerAlign: "center",
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "is_compliance",
          text: "Compliance Manager",
          sort: true,
          sortCaret,
          align: "center",
          headerAlign: "center",
          style: { width: "127px" },
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "is_senior_manager",
          text: "Senior Manager",
          sort: true,
          sortCaret,
          align: "center",
          headerAlign: "center",
          style: { width: "127px" },
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "action",
          text: "Action",
          align: "center",
          headerAlign: "center",
          style: { width: "127px" },
        },
      ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC",
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}${governance ? "&tab=governance-framework" : ""}`,
      {
        replace: true,
      },
    );
  }, [
    navigate,
    limit,
    searchParam,
    page,
    sortField,
    sortOrder,
    governance,
  ]);

  useEffect(() => {
    organizationData?.organizationId && fetchUser();
  }, [organizationData?.organizationId, fetchUser]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    [],
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchUser(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchUser(limit, page);
  };

  // Single User Delete
  const handleUserDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.USER}/${userId}`);
      setLoading(false);
      fetchUser();
    } catch (error) {
      setLoading(false);
    }
  };

  // User Resend
  const handleUserResend = async () => {
    try {
      setLoading(true);
      await get(`${API.USER_RESEND_INVITATION}/${userId}`);
      setLoading(false);
      fetchUser();
    } catch (error) {
      setLoading(false);
    }
  };

  return (organizationData?.organizationData?.is_subscribed || organizationData?.organizationData?.is_aml_ctf_subscribed) ? (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContentDirect={true}
        expandList={expandList}
        actionContent={
          !governance && (
            <>
              <div className="w-[500px]">
                {userAllowedData?.limit ? (
                  <span>
                    Current Limit:{" "}
                    <b className="mr-3">
                      {userAllowedData?.limit ? userAllowedData?.limit : 0}
                    </b>
                    Current Usage:{" "}
                    <b>{userAllowedData?.usage ? userAllowedData?.usage : 0}</b>
                    {userAllowedData?.usage >= userAllowedData?.limit && (
                      <Button
                        className="ml-3"
                        onClick={() => {
                          navigate(`/general-settings?tab=subscription`);
                        }}
                        variant="destructive"
                      >
                        Upgrade Plan
                      </Button>
                    )}
                  </span>
                ) : (
                  <b>Unlimited</b>
                )}
              </div>
              {/* Add User */}
              {canCreate || edit === true ? (
                <ResponsiveDrawerForm
                  description={""}
                  title={edit ? "Edit User" : "Add User"}
                  handleOpen={() => {}}
                  handleClose={() => {
                    setEdit(false);
                    setUserId(null);
                    setShowAddEditUser(false);
                  }}
                  buttonText={
                    <div className="flex items-center gap-1">
                      <LucidePlusCircle size={18} />
                      <span>Add User</span>
                    </div>
                  }
                  className={""}
                  open={showAddEditUser}
                  buttonClassName={""}
                  setOpen={setShowAddEditUser}
                  write={true}
                >
                  {organizationData?.organizationId && (
                    <AddEditUser
                      edit={edit}
                      toggleShowAddEditUser={toggleShowAddEditUser}
                      id={userId}
                      orgId={
                        organizationData?.organizationId
                          ? organizationData?.organizationId
                          : ""
                      }
                      fetchUserData={() => fetchUser(limit, page)}
                    />
                  )}
                </ResponsiveDrawerForm>
              ) : (
                <Button
                  onClick={() => {
                    toast({
                      variant: "success",
                      description:
                        "Please upgrade your plan to add more users.",
                      duration: 3000,
                    });
                  }}
                >
                  <div className="flex items-center gap-1">
                    <LucidePlusCircle size={18} />
                    <span>Add User</span>
                  </div>
                </Button>
              )}
            </>
          )
        }
        loading={loading}
        innerBody={governance}
        parentHeight={governance}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={users}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={(row) => {
                if (row?.deleted_at) {
                  return `${TRClasses} bg-muted dark:text-red-400 dark:bg-muted/50 text-red-600`;
                }
                return TRClasses;
              }}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete User"}
        open={showUserDelete}
        setOpen={setShowUserDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete user?"}
        okAction={async () => {
          handleUserDelete();
          toggleShowDelete();
          fetchUser();
        }}
        cancelAction={() => {
          setUserId(null);
          setShowUserDelete(!showUserDelete);
        }}
      />

      <Confirmation
        title={"Resend User Invitation"}
        open={showUserResend}
        setOpen={setShowUserResend}
        toggleShow={() => {
          toggleShowResend();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to resend user invitation?"}
        okAction={async () => {
          handleUserResend();
          toggleShowResend();
        }}
        cancelAction={() => {
          setUserId(null);
          setShowUserResend(!showUserResend);
        }}
      />
    </>
  ) : (
    <UnauthorisedAccess />
  );
};

export default Users;
